import React, { useEffect, useMemo, useState } from 'react';
import { MessageCircle, Clock, X, CheckCircle, UserCheck, Search } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { acceptChat } from '../../api/chatService';
import { useAgentLiveChat } from '../../contexts/AgentLiveChatContext';
import toast from 'react-hot-toast';

const requestCreatedMs = (request) => {
  if (request?.createdOn) {
    const t = new Date(request.createdOn).getTime();
    if (!Number.isNaN(t)) return t;
  }
  if (typeof request?.timestamp === 'number') return request.timestamp;
  return 0;
};

/**
 * Live Support Requests — read-only list; state lives in AgentLiveChatContext.
 * Search/sort are client-only over already-loaded rows.
 * @param {{ className?: string }} [props]
 */
const LiveSupportRequests = ({ className } = {}) => {
  const {
    incomingRequests,
    refreshPendingFromApi,
    removeIncomingRequest,
    setActiveChatSession
  } = useAgentLiveChat();

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState('newest');

  const pendingCount = useMemo(
    () => incomingRequests.filter((r) => r.queueState !== 'accepted_elsewhere').length,
    [incomingRequests]
  );

  const displayedRequests = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    let list = incomingRequests;
    if (q) {
      list = list.filter((r) => {
        const name = (r.guestName || '').toLowerCase();
        const email = (r.email || '').toLowerCase();
        const subject = (r.subject || '').toLowerCase();
        return name.includes(q) || email.includes(q) || subject.includes(q);
      });
    }
    const sorted = [...list].sort((a, b) => {
      const diff = requestCreatedMs(a) - requestCreatedMs(b);
      return sortOrder === 'newest' ? -diff : diff;
    });
    return sorted;
  }, [incomingRequests, searchTerm, sortOrder]);

  useEffect(() => {
    refreshPendingFromApi();
  }, [refreshPendingFromApi]);

  const getTimeElapsed = (createdOn) => {
    if (!createdOn) return 'Just now';
    try {
      const created = new Date(createdOn);
      const now = new Date();
      const diffMs = now - created;
      const diffMins = Math.floor(diffMs / 60000);
      const diffSecs = Math.floor((diffMs % 60000) / 1000);
      if (diffMins > 0) {
        return `${diffMins}m ${diffSecs}s ago`;
      }
      return `${diffSecs}s ago`;
    } catch {
      return 'Just now';
    }
  };

  const handleAccept = async (chatRequest) => {
    setLoading(true);
    try {
      const response = await acceptChat(chatRequest.chatSessionId);

      if (response.success && response.data) {
        removeIncomingRequest(chatRequest.chatSessionId);
        setActiveChatSession(response.data);
        toast.success('Chat session accepted successfully');
      } else {
        throw new Error(response.message || 'Failed to accept chat session');
      }
    } catch (error) {
      console.error('Error accepting chat:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to accept chat session');
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = (chatRequest) => {
    removeIncomingRequest(chatRequest.chatSessionId);
    toast.success('Chat request dismissed');
  };

  const inputClass =
    'w-full min-w-0 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100';

  return (
    <Card
      className={cn(
        'min-h-0 min-w-0 flex flex-col h-full max-h-[min(480px,55vh)] overflow-hidden',
        className
      )}
    >
      <CardHeader className="p-3 pb-2 shrink-0 space-y-2">
        <CardTitle className="text-base font-semibold flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <MessageCircle className="h-5 w-5 text-blue-500 shrink-0" />
            <span className="truncate">Live Support Requests</span>
          </div>
          <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 shrink-0">
            {pendingCount}
          </Badge>
        </CardTitle>

        <div className="flex items-center gap-2 min-w-0">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search..."
              aria-label="Search live support requests"
              className={cn(inputClass, 'pl-7')}
            />
          </div>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            aria-label="Sort live support requests"
            className={cn(inputClass, 'w-auto shrink-0 max-w-[42%] sm:max-w-none')}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>
        </div>
      </CardHeader>

      <CardContent className="p-3 pt-0 flex-1 flex flex-col min-h-0 overflow-hidden">
        <div className="min-h-0 flex-1 overflow-y-auto">
          {incomingRequests.length === 0 ? (
            <div className="text-center py-6 text-gray-500 flex flex-col justify-center min-h-[8rem]">
              <MessageCircle className="h-10 w-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No pending chat requests</p>
              <p className="text-xs mt-1 text-gray-400">New requests will appear here in real-time</p>
            </div>
          ) : displayedRequests.length === 0 ? (
            <div className="text-center py-6 text-gray-500">
              <p className="text-sm">No matching requests</p>
              <p className="text-xs mt-1 text-gray-400">Try a different search</p>
            </div>
          ) : (
            <div className="space-y-2">
              {displayedRequests.map((request) => {
                const isElsewhere = request.queueState === 'accepted_elsewhere';
                return (
                  <div
                    key={request.chatSessionId}
                    className={cn(
                      'rounded-lg border p-2.5 sm:p-3 transition-colors',
                      isElsewhere
                        ? 'border-slate-300 bg-slate-50 dark:border-slate-600 dark:bg-slate-900/40'
                        : 'border-border-light hover:bg-gray-50 dark:border-border-dark dark:hover:bg-gray-800'
                    )}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        {isElsewhere && (
                          <div className="mb-2 flex flex-wrap items-center gap-2">
                            <Badge className="bg-emerald-100 text-emerald-900 dark:bg-emerald-900 dark:text-emerald-100">
                              Accepted / In progress
                            </Badge>
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-700 dark:text-slate-200">
                              <UserCheck className="h-3.5 w-3.5 shrink-0" />
                              {request.acceptedByAgentName || 'Another agent'}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-semibold text-text-primary-light dark:text-text-primary-dark">
                            {request.guestName || 'Guest User'}
                          </h3>
                          {request.ticketNumber && (
                            <Badge variant="outline" className="text-xs">
                              {request.ticketNumber}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-2 break-words">
                          {request.subject || 'General Inquiry'}
                        </p>
                        {request.email && !isElsewhere && (
                          <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mb-2">
                            {request.email}
                          </p>
                        )}
                        <div className="flex items-center gap-1 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                          <Clock className="h-3 w-3 shrink-0" />
                          <span>{getTimeElapsed(request.createdOn)}</span>
                        </div>
                        {isElsewhere && (
                          <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                            This chat was claimed by another agent. You can dismiss this notice.
                          </p>
                        )}
                      </div>
                    </div>
                    {isElsewhere ? (
                      <div className="mt-2 flex justify-end">
                        <Button
                          type="button"
                          onClick={() => removeIncomingRequest(request.chatSessionId)}
                          variant="outline"
                          size="sm"
                        >
                          Dismiss
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 mt-2">
                        <Button
                          type="button"
                          onClick={() => handleAccept(request)}
                          disabled={loading}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                          size="sm"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Accept
                        </Button>
                        <Button
                          type="button"
                          onClick={() => handleDecline(request)}
                          disabled={loading}
                          variant="outline"
                          size="sm"
                          className="flex-1"
                        >
                          <X className="h-4 w-4 mr-1" />
                          Decline
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default LiveSupportRequests;
